import { Controller } from "@hotwired/stimulus";

// Only one mount exists on this page (see clusters/spec_map), so nothing here needs an Outlet.
// Validation-message targets stay wrapped in jQuery when handed to
// window.ltFormValidators.showValidationErrorMessage, which is `($message) => $message.show()`
// in app/assets/javascripts/lt_form_validators.js -- that shared helper also serves
// validation_controller.js and platform_license_edit, so it is not this controller's to change
// (decisions D3).
export default class extends Controller {
  static get targets() {
    return [
      "creditsForm", "debitsForm",
      "creditsReason", "creditsNotesContainer", "creditsCashContainer",
      "creditsCredit", "creditsNotes", "creditsCashAmount", "creditsExpirationDate",
      "debitsReason", "debitsNotesContainer", "debitsOrgFieldsContainer",
      "debitsTransferOrgId", "debitsTransferOrgName", "debitsNotes", "debitsCredit",
      "creditsReasonValidationMessage", "creditsNotesValidationMessage",
      "creditsCreditAmountValidationMessage", "creditsCashAmountValidationMessage",
      "creditsExpirationDateValidationMessage",
      "debitsReasonValidationMessage", "debitsNotesValidationMessage",
      "debitsCreditAmountValidationMessage", "debitsTransferOrgIdValidationMessage"
    ];
  }

  static get values() { return { orgInfoUrl: String }; }

  connect() {
    this.validTransferOrgId = false;
    this.lookupTransferOrgDebounced = this.debounce((value) => this.lookupTransferOrg(value), 333);

    // The sitewide `input.datepicker` initializer in initializers.js.erb races this one and
    // usually wins, and an object-form `.datepicker(options)` call is a no-op once the field has
    // an instance. `setStartDate` goes through the plugin's string-option branch unconditionally,
    // so it applies either way. Personal organizations don't render this field at all (see the
    // `unless @organization.personal?` guard in the view), so it's a real optional target, not a
    // hard one -- see pitfalls P8/the R2 target caution for this unit.
    if (this.hasCreditsExpirationDateTarget) {
      $(this.creditsExpirationDateTarget).datepicker({ startDate: new Date() });
      $(this.creditsExpirationDateTarget).datepicker("setStartDate", new Date());
    }
  }

  disconnect() {
    clearTimeout(this.transferOrgLookupTimer);
  }

  debounce(callback, ms) {
    return (...args) => {
      clearTimeout(this.transferOrgLookupTimer);
      this.transferOrgLookupTimer = setTimeout(() => callback(...args), ms);
    };
  }

  onAdjustmentTypeChange(e) {
    this.displayForm(e.currentTarget.value);
  }

  onCreditsReasonChange(e) {
    const reason = e.currentTarget.value;
    this.toggleCreditsNotes(this.showCreditsNotes(reason));
    this.toggleCreditsCash(this.requiresCashValue(reason));

    if (reason === "holiday_credits") {
      this.creditsCreditTarget.value = "";
      this.setHolidayExpirationDate();
    }
  }

  onDebitsReasonChange(e) {
    const value = e.currentTarget.value;
    this.toggleDebitsNotes(this.showDebitsNotes(value));
    this.toggleDebitsOrgFields(this.showDebitsOrgFields(value));
  }

  onDebitsTransferOrgIdChange(e) {
    this.lookupTransferOrg(e.currentTarget.value.trim());
  }

  onDebitsTransferOrgIdInput(e) {
    this.lookupTransferOrgDebounced(e.currentTarget.value.trim());
  }

  onCreateCreditClick(e) {
    if (!this.isCreditFormValid()) {
      e.preventDefault();
    }
  }

  onCreateDebitClick(e) {
    if (!this.isDebitFormValid()) {
      e.preventDefault();
    }
  }

  displayForm(formName) {
    switch (formName) {
      case "credit":
        this.show(this.creditsFormTarget);
        this.hide(this.debitsFormTarget);
        break;
      case "debit":
        this.hide(this.creditsFormTarget);
        this.show(this.debitsFormTarget);
        break;
      default:
        this.hide(this.creditsFormTarget);
        this.hide(this.debitsFormTarget);
    }
  }

  toggleDebitsNotes(display) {
    display ? this.show(this.debitsNotesContainerTarget) : this.hide(this.debitsNotesContainerTarget);
  }

  toggleCreditsNotes(display) {
    display ? this.show(this.creditsNotesContainerTarget) : this.hide(this.creditsNotesContainerTarget);
  }

  toggleCreditsCash(display) {
    display ? this.show(this.creditsCashContainerTarget) : this.hide(this.creditsCashContainerTarget);
  }

  toggleDebitsOrgFields(display) {
    if (display) {
      this.show(this.debitsOrgFieldsContainerTarget);
    } else {
      this.hide(this.debitsOrgFieldsContainerTarget);
      this.debitsTransferOrgIdTarget.value = "";
      this.debitsTransferOrgNameTarget.value = "";
      this.validTransferOrgId = false;
    }
  }

  // jQuery 3's `.show()` sets an explicit inline `display` value to win over the stylesheet's
  // `display: none` (app/assets/stylesheets/admin/account_adjustments.scss) -- inline style beats
  // a plain selector rule, but an empty string would just cede back to the cascade. Every element
  // these two toggle is a `div`, whose default display is "block", so this reproduces that exactly.
  show(el) { el.style.display = "block"; }
  hide(el) { el.style.display = "none"; }

  showDebitsNotes(value) { return ["other", "duties", "adjustment_org_transfer"].includes(value); }
  showDebitsOrgFields(value) { return ["adjustment_org_transfer"].includes(value); }
  showCreditsNotes(value) { return ["other", "adjustment_org_transfer"].includes(value); }
  requiresCashValue(reason) { return ["invoice", "holiday_credits"].includes(reason); }

  // Next January 31st from the given date.
  getNextJanuary31(fromDate) {
    const date = new Date(fromDate);
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();
    const targetYear = currentMonth === 0 ? currentYear : currentYear + 1;

    return new Date(targetYear, 0, 31);
  }

  setHolidayExpirationDate() {
    if (!this.hasCreditsExpirationDateTarget) { return; }

    const nextJan31 = this.getNextJanuary31(new Date());
    const formattedDate = (nextJan31.getMonth() + 1).toString().padStart(2, "0") + "/" +
      nextJan31.getDate().toString().padStart(2, "0") + "/" +
      nextJan31.getFullYear();
    this.creditsExpirationDateTarget.value = formattedDate;
  }

  lookupTransferOrg(orgId) {
    if (!orgId) {
      this.debitsTransferOrgNameTarget.value = "";
      this.validTransferOrgId = false;
      return;
    }

    fetch(`${this.orgInfoUrlValue}?id=${orgId}`)
      .then((response) => {
        if (!response.ok) { throw new Error("organization lookup failed"); }
        return response.json();
      })
      .then((data) => {
        this.debitsTransferOrgNameTarget.value = data.name;
        this.validTransferOrgId = true;
      })
      .catch(() => {
        this.debitsTransferOrgNameTarget.value = "";
        this.validTransferOrgId = false;
      });
  }

  isCreditFormValid() {
    let valid = true;

    const creditsReason = this.creditsReasonTarget.value;
    const creditsNotes = this.creditsNotesTarget.value;
    const creditsCreditAmount = this.creditsCreditTarget.value;
    const creditsCashAmount = this.creditsCashAmountTarget.value;
    const creditsExpirationDate = this.hasCreditsExpirationDateTarget ? this.creditsExpirationDateTarget.value : undefined;
    const shouldValidateExpirationDate = this.hasCreditsExpirationDateValidationMessageTarget;

    if (window.ltFormValidators.isBlank(creditsReason)) {
      window.ltFormValidators.showValidationErrorMessage($(this.creditsReasonValidationMessageTarget));
      valid = false;
    }

    if (creditsReason === "other" && window.ltFormValidators.isBlank(creditsNotes)) {
      window.ltFormValidators.showValidationErrorMessage($(this.creditsNotesValidationMessageTarget));
      valid = false;
    }

    if (!window.ltFormValidators.isNumber(creditsCreditAmount)) {
      window.ltFormValidators.showValidationErrorMessage($(this.creditsCreditAmountValidationMessageTarget));
      valid = false;
    }

    if (this.requiresCashValue(creditsReason) && (window.ltFormValidators.isBlank(creditsCashAmount) || !window.ltFormValidators.isNumber(creditsCashAmount))) {
      window.ltFormValidators.showValidationErrorMessage($(this.creditsCashAmountValidationMessageTarget));
      valid = false;
    }

    if (shouldValidateExpirationDate && window.ltFormValidators.isBlank(creditsExpirationDate)) {
      window.ltFormValidators.showValidationErrorMessage($(this.creditsExpirationDateValidationMessageTarget));
      valid = false;
    }

    return valid;
  }

  isDebitFormValid() {
    let valid = true;

    const debitsReason = this.debitsReasonTarget.value;
    const debitsNotes = this.debitsNotesTarget.value;
    const debitsCreditAmount = this.debitsCreditTarget.value;

    if (window.ltFormValidators.isBlank(debitsReason)) {
      window.ltFormValidators.showValidationErrorMessage($(this.debitsReasonValidationMessageTarget));
      valid = false;
    }

    if (debitsReason === "other" && window.ltFormValidators.isBlank(debitsNotes)) {
      window.ltFormValidators.showValidationErrorMessage($(this.debitsNotesValidationMessageTarget));
      valid = false;
    }

    if (debitsReason === "adjustment_org_transfer" && !this.validTransferOrgId) {
      window.ltFormValidators.showValidationErrorMessage($(this.debitsTransferOrgIdValidationMessageTarget));
      valid = false;
    }

    if (!window.ltFormValidators.isNumber(debitsCreditAmount) || Number(debitsCreditAmount) >= 0) {
      window.ltFormValidators.showValidationErrorMessage($(this.debitsCreditAmountValidationMessageTarget));
      valid = false;
    }

    return valid;
  }
}
